import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../lib/firebase/auth-context.js';
import { useFeedbackUnread } from '../../lib/hooks/useFeedbackUnread.js';
import { FeedbackThread } from './FeedbackThread.js';
import { Spinner } from '../shared/Spinner.js';
import { EmptyState } from '../shared/EmptyState.js';
import { timeAgo } from '../../lib/utils/time.js';
import {
  FEEDBACK_CATEGORY_LABEL,
  FEEDBACK_STATUS_LABEL,
  type Feedback,
} from '../../types/feedback.js';

/**
 * 投稿者向け「やり取り」一覧。自分が送ったフィードバックを新しい順に並べ、
 * 行クリックでスレッド（FeedbackThread）を展開する。展開時に既読化する。
 */
export function MyFeedbackList() {
  const { profile } = useAuth();
  const uid = profile?.id ?? null;
  const qc = useQueryClient();
  const { feedbacks, isPending, isUnread, markRead } = useFeedbackUnread(uid);
  const [openId, setOpenId] = useState<string | null>(null);

  if (isPending) {
    return (
      <div className="fb-mylist-loading">
        <Spinner />
      </div>
    );
  }

  if (feedbacks.length === 0) {
    return (
      <EmptyState
        title="まだフィードバックを送っていません"
        description="「送る」タブから、ご要望や不具合を送れます。"
      />
    );
  }

  const toggle = (fb: Feedback) => {
    if (openId === fb.id) {
      setOpenId(null);
    } else {
      setOpenId(fb.id);
      markRead(fb);
    }
  };

  return (
    <div className="fb-mylist">
      {feedbacks.map((fb) => {
        const open = openId === fb.id;
        const unread = isUnread(fb);
        return (
          <div key={fb.id} className="fb-mylist-item">
            <button
              type="button"
              className="fb-mylist-row"
              aria-expanded={open}
              onClick={() => toggle(fb)}
            >
              {unread && <span className="fb-unread-dot" aria-label="未読" />}
              <span className="badge" data-status={fb.category}>
                {FEEDBACK_CATEGORY_LABEL[fb.category]}
              </span>
              <span className="fb-mylist-msg">{fb.message}</span>
              <span className="fb-mylist-status">
                {FEEDBACK_STATUS_LABEL[fb.status]}
              </span>
              <span className="fb-mylist-time">{timeAgo(fb.createdAt)}</span>
            </button>
            {open && uid && (
              <FeedbackThread
                feedbackId={fb.id}
                feedbackOwnerUid={uid}
                viewerRole="user"
                onSent={() =>
                  qc.invalidateQueries({ queryKey: ['feedbacks', 'mine', uid] })
                }
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
